import { useCallback, useEffect, useRef, useState } from "react";

function toSeconds(minutes: number): number {
  return Math.max(0, Math.floor(minutes)) * 60;
}

/**
 * useCountdownTimer - generic countdown timer hook
 * @param minutesInit Initial minutes
 * @param onExpire Callback when timer expires
 */
export function useCountdownTimer(minutesInit: number, onExpire?: () => void) {
  const initialSeconds = toSeconds(minutesInit);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(initialSeconds);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onExpireRef = useRef(onExpire);
  const hasExpiredRef = useRef(initialSeconds === 0);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setInterval(() => {
      setRemainingSeconds((prevSeconds) => {
        if (prevSeconds <= 1) {
          clearTimer();
          return 0;
        }
        return prevSeconds - 1;
      });
    }, 1000);
  }, [clearTimer]);

  const resetTimer = useCallback(
    (minutes: number = minutesInit) => {
      const nextRemaining = toSeconds(minutes);
      hasExpiredRef.current = nextRemaining === 0;
      setRemainingSeconds(nextRemaining);
      if (nextRemaining > 0) {
        startTimer();
      } else {
        clearTimer();
      }
    },
    [clearTimer, minutesInit, startTimer],
  );

  useEffect(() => {
    if (initialSeconds > 0) {
      startTimer();
    }
    return () => {
      clearTimer();
    };
  }, [clearTimer, initialSeconds, startTimer]);

  useEffect(() => {
    if (remainingSeconds === 0 && !hasExpiredRef.current) {
      hasExpiredRef.current = true;
      onExpireRef.current?.();
    }
  }, [remainingSeconds]);

  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  const setMinutes = useCallback((minutesValue: number) => {
    const nextSeconds = toSeconds(minutesValue) + seconds;
    hasExpiredRef.current = nextSeconds === 0;
    setRemainingSeconds(nextSeconds);
  }, [seconds]);

  const setSeconds = useCallback((secondsValue: number) => {
    const boundedSeconds = Math.max(0, Math.min(59, Math.floor(secondsValue)));
    const nextSeconds = minutes * 60 + boundedSeconds;
    hasExpiredRef.current = nextSeconds === 0;
    setRemainingSeconds(nextSeconds);
  }, [minutes]);

  return { minutes, seconds, setMinutes, setSeconds, resetTimer };
}
